import { QueryClientProvider } from "@tanstack/react-query";
// import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import {
  ensureApplicationIdPersisted,
  initApplicationId,
  persistNativeApplicationId,
} from "./lib/applicationId";
import { setupGa4UserSync } from "./lib/ga4";
import { applyDocumentLanguage } from "./lib/language";
import { queryClient } from "./lib/queryClient";
import { ROUTER_BASEPATH } from "./lib/routerBasepath";
import { initSentry } from "./lib/sentry/instrument";
import { bootstrapTheme } from "./lib/theme";
import { setupScreenRenderTracking } from "./lib/tracking/screenRenderTracking";
import { routeTree } from "./routeTree.gen";
import { resolveProfileViewTransitionTypes } from "./utils/profileViewTransition";
import "./index.css";

// 初回ペイントから正しいテーマカラーで描画するため、render 前に localStorage の
// application_id を確定させてからテーマを :root に書き込む（Utage-Web 運用と同じ）。
if (typeof window !== "undefined") {
  persistNativeApplicationId(new URLSearchParams(window.location.search));
  ensureApplicationIdPersisted();
}
bootstrapTheme();

// 本番は ROUTER_BASEPATH が "" → undefined ＝無指定（従来どおり）
const router = createRouter({
  routeTree,
  basepath: ROUTER_BASEPATH || undefined,
  context: { queryClient },
  defaultPreload: "intent",
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // 内側のスクロール領域は、遷移元画面のスクロール量が同じセレクタの要素へ持ち越されるため、
  // 新規遷移では data-scroll-to-top を付けた要素を最上部に戻す（戻る・進むでは復元される）
  scrollToTopSelectors: ["window", "[data-scroll-to-top]"],
  // プロフィール画面の出入りだけスライドさせる（他の遷移は false で View Transition 自体を起こさない）
  defaultViewTransition: { types: resolveProfileViewTransitionTypes },
});

initSentry(router);

// 画面描画時間の計測（Sentry ui.render スパン）と Firebase の screen_view を全ルート遷移で一括配線する
// （各画面に手書きしない。画面側は useScreenReady で「主データが揃った」を宣言するだけ）
setupScreenRenderTracking(router);

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Root element #root not found");
}

await initApplicationId(new URLSearchParams(window.location.search));
// applicationId 確定後に GA4（Sakura / Renka Android 直送）のユーザー同期を開始する
setupGa4UserSync();
// sina は国内専用のため ja 固定で document の言語・書字方向を設定する
applyDocumentLanguage("ja");

createRoot(rootElement).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  </StrictMode>,
);
